/**
 * Edit / delete / restore for support tickets and their staff messages.
 *
 * The backend is the real boundary (RBAC module `support_manage`); everything
 * here decides what the UI SHOWS, and how it degrades against a backend that
 * has not shipped these endpoints yet.
 */

import { ApiError } from './api';

/** The RBAC module: read = see deleted tickets + audit, write = change things. */
export const SUPPORT_MANAGE = 'support_manage';
export const RESTORE_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** What the backend says the caller may do on ONE ticket. Absent = older backend. */
export interface Capabilities {
  canEdit: boolean;
  canDelete: boolean;
  canManageMessages: boolean;
  ownMessageIds: string[];
}

export function parseCapabilities(raw: unknown): Capabilities | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const c = raw as Record<string, unknown>;
  return {
    canEdit: c.canEdit === true,
    canDelete: c.canDelete === true,
    canManageMessages: c.canManageMessages === true,
    ownMessageIds: Array.isArray(c.ownMessageIds) ? c.ownMessageIds.map(String) : [],
  };
}

/** "#1a2b3c4d — Title": how a ticket is named on screen and in the confirm. */
export function ticketRef(t: { id: string; title: string }): string {
  return `#${t.id.slice(0, 8)} — ${t.title}`;
}

/** When a deleted ticket stops being restorable: the server's value, else
 *  deleted_at + 30 days. Null for a live ticket. */
export function restoreDeadline(t: { deleted_at?: string | null; restorable_until?: string | null }): Date | null {
  if (!t.deleted_at) return null;
  const given = t.restorable_until ? new Date(t.restorable_until) : null;
  if (given && !isNaN(given.getTime())) return given;
  const d = new Date(t.deleted_at);
  return isNaN(d.getTime()) ? null : new Date(d.getTime() + RESTORE_WINDOW_DAYS * DAY_MS);
}

export function restoreWindowEnded(
  t: { deleted_at?: string | null; restorable_until?: string | null }, now: number = Date.now(),
): boolean {
  const end = restoreDeadline(t);
  return !!end && now > end.getTime();
}

/** The API's machine-readable code, wherever the backend put it. */
export function errorCode(err: unknown): string {
  const b = (err as ApiError | undefined)?.body;
  return String(b?.error ?? b?.code ?? '');
}

export function isRestoreWindowEnded(err: unknown): boolean {
  return (err as ApiError)?.status === 409 && (errorCode(err) === 'restore_window_ended' || /restore_window_ended/.test((err as Error).message || ''));
}

/**
 * A 404 from a route that does not exist (an older backend) as opposed to a 404
 * from a real handler ("Ticket not found"). Express answers a missing route with
 * "Cannot PATCH /path" or a bare "Not Found".
 */
export function isMissingEndpoint(err: unknown): boolean {
  if ((err as ApiError)?.status !== 404) return false;
  const msg = String((err as Error).message || '').trim();
  return !msg || /^not found$/i.test(msg) || /cannot (get|post|put|patch|delete)\b/i.test(msg);
}

export const MISSING_ENDPOINT_MESSAGE =
  'The support backend does not offer this yet, so the action is unavailable.';

/** The `reason` shown on a disabled action. */
export const NEEDS_WRITE_REASON = 'You need write access to the "support_manage" module to do this.';

export interface AuditRow {
  id: string;
  ticket_id: string | null;
  response_id: string | null;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  field: string | null;
  previous_value: string | null;
  new_value: string | null;
  created_at: string;
}

const str = (v: unknown): string | null => (v == null ? null : typeof v === 'string' ? v : JSON.stringify(v));

/** Tolerant of `[rows]` or `{ audit|rows|entries: [rows] }`; drops junk rows. */
export function parseAudit(raw: unknown): AuditRow[] {
  const list = Array.isArray(raw) ? raw
    : Array.isArray((raw as any)?.audit) ? (raw as any).audit
    : Array.isArray((raw as any)?.rows) ? (raw as any).rows
    : Array.isArray((raw as any)?.entries) ? (raw as any).entries : [];
  return list
    .filter((r: any) => r && typeof r === 'object' && typeof r.action === 'string')
    .map((r: any) => ({
      id: String(r.id ?? `${r.action}-${r.created_at}`),
      ticket_id: str(r.ticket_id), response_id: str(r.response_id),
      action: r.action, actor_id: str(r.actor_id), actor_name: str(r.actor_name),
      field: str(r.field), previous_value: str(r.previous_value), new_value: str(r.new_value),
      created_at: String(r.created_at ?? ''),
    }));
}

/** Only the fields whose value differs from the original — the PATCH body. */
export function changedFields<T extends Record<string, unknown>>(before: T, after: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(after) as (keyof T)[]) {
    if (after[k] !== before[k]) out[k] = after[k];
  }
  return out;
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** A short human line for one audit row. */
export function auditSummary(r: AuditRow): string {
  const label = r.action.replace(/_/g, ' ');
  if (r.field) return `${label} — ${r.field}: ${r.previous_value ?? '(empty)'} → ${r.new_value ?? '(empty)'}`;
  return label;
}
